import { useState } from 'react';
import { provider } from '../../helpers/client';

type GetLimitsParams = {
    monthlyIncome: number;
    okb?: number;
};

interface CachedParams extends Partial<GetLimitsParams> {
    prevRawAmount: number;
    isFetching: boolean;
}

const DEFAULT_LIMITS = {
    WITHOUT_OKB: 19_000,
    WITH_OKB: 20_000,
};

const cachedParams: CachedParams = { prevRawAmount: 0, isFetching: false };

export const useGetLimits = () => {
    const [inProcess, setInProcess] = useState(false);
    const [amount, setAmount] = useState(0);

    const areParamsSame = (params: GetLimitsParams) =>
        Boolean(cachedParams.monthlyIncome === params.monthlyIncome && cachedParams.okb === params.okb);

    const waitUntilFetching = (timeout = 5000) => {
        let passedTime = 0;
        const step = 200;
        const poll = (resolve: any) => {
            if (!cachedParams.isFetching || timeout <= passedTime) resolve();
            else {
                passedTime += step;
                setTimeout((_: any) => poll(resolve), step);
            }
        };

        return new Promise(poll);
    };

    const getScore = async (params: GetLimitsParams) => {
        cachedParams.isFetching = true;
        cachedParams.monthlyIncome = params.monthlyIncome;
        cachedParams.okb = params.okb;
        const [data] = await provider.getModelsScore(params);
        const newLimit = data?.prediction || (params.okb ? DEFAULT_LIMITS.WITH_OKB : DEFAULT_LIMITS.WITHOUT_OKB);
        cachedParams.prevRawAmount = newLimit;
        cachedParams.isFetching = false;
        return newLimit;
    };

    const getLimits = async (params: GetLimitsParams, step = 1) => {
        if (!params.monthlyIncome) {
            return;
        }
        setInProcess(true);
        try {
            if (cachedParams.isFetching) {
                await waitUntilFetching();
            }
            const rawAmount = areParamsSame(params) ? cachedParams.prevRawAmount : await getScore(params);
            const calculatedAmount = Math.trunc(rawAmount / Math.pow(0.93, step) / 1000) * 1000;
            setAmount(calculatedAmount);
        } finally {
            setInProcess(false);
        }
    };

    return { inProcess, getLimits, amount };
};

export const getLimitForAuthUser = async (monthlyIncome: number, okb?: number) => {
    const [data] = await provider.getModelsScore({ okb, monthlyIncome });
    const newLimit = data?.prediction || (okb ? DEFAULT_LIMITS.WITH_OKB : DEFAULT_LIMITS.WITHOUT_OKB);
    const calculatedAmount = Math.trunc(newLimit / Math.pow(0.93, 4) / 1000) * 1000;
    return calculatedAmount;
};
